<section class="requests-page" aria-labelledby="page-title">
  <header class="flex align-center justify-between gap-15 mb-20">
    <div>
      <h1 id="page-title" class="page-title fs-22 fw-700 m-0">New Linking Requests</h1>
      <p class="page-subtitle m-0 mt-5">
        Portal accounts that could not be automatically matched to a patient record and require
        manual review.
      </p>
    </div>
    <!-- CSV export was removed from scope; the action stays visible but disabled. -->
    <button
      type="button"
      class="primary-button"
      disabled
      aria-label="Export requests (currently unavailable)"
    >
      <app-icon
        name="download"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      />
      Export
    </button>
  </header>

  <section class="queue-card" aria-labelledby="queue-heading">
    <h2 id="queue-heading" class="sr-only">New linking requests queue</h2>

    <div class="filter-bar flex gap-12 align-center flex-wrap">
      <app-dropdown
        label="Status"
        ariaLabel="Filter requests by status"
        [options]="statusFilterOptions"
        [value]="selectedStatus()"
        (valueChange)="onStatusChange($event)"
      />

      <app-dropdown
        label="Assigned To"
        ariaLabel="Filter requests by assignee"
        [options]="assignedToFilterOptions()"
        [value]="selectedAssignedTo()"
        (valueChange)="onAssignedToChange($event)"
      />

      <app-dropdown
        label="Date Range"
        ariaLabel="Filter requests by date range"
        [options]="dateRangeOptions"
        [value]="selectedDateRange()"
        (valueChange)="onDateRangeChange($event)"
      />

      <div class="filter-divider" aria-hidden="true"></div>

      <div class="filter-control--search">
        <app-input label="Search" inputId="request-search" icon="search">
          <input
            id="request-search"
            type="search"
            [ngModel]="searchTerm()"
            (ngModelChange)="onSearchChange($event)"
            (keydown.enter)="applyFilters()"
            placeholder="Search by name, DOB, or identifier"
            aria-label="Search by patient name, date of birth, or identifier"
            [attr.aria-invalid]="searchValidationError() ? 'true' : null"
            [attr.aria-describedby]="searchValidationError() ? 'search-term-error' : null"
          />
          @if (searchTerm()) {
          <app-icon-button
            class="search-clear"
            icon="close"
            size="xs"
            ariaLabel="Clear search"
            color="on-surface-variant"
            (clicked)="clearSearch()"
          />
          }
        </app-input>
        @if (searchValidationError(); as validationMessage) {
        <p id="search-term-error" class="search-validation-error m-0 mt-5 fs-12" role="alert">
          {{ validationMessage }}
        </p>
        }
      </div>

      <button
        type="button"
        class="primary-button button--compact filter-apply"
        (click)="applyFilters()"
        [disabled]="isLoading()"
      >
        Filter
      </button>
    </div>

    <!-- The shell is measured to size the page (NewLinkingRequests.TABLE_CHROME_PX)
         and the queue's first load waits on that measurement, so it must stay
         outside the state branches below: measuring the row container instead
         would need rows on screen first, which costs a second PHI-bearing
         request per visit. -->
    <div class="table-shell" #tableShell>
      @if (isLoading()) {
      <div
        class="table-status flex flex-col align-center justify-center text-center"
        role="status"
        aria-live="polite"
      >
        <app-progress-spinner [diameter]="48" />
        <p>Loading linking requests…</p>
      </div>
      } @else if (loadError(); as loadError) {
      <div
        class="table-status table-status--error flex flex-col align-center justify-center text-center"
        aria-live="polite"
      >
        <h3>Unable to load requests</h3>
        <p>{{ loadError }}</p>
      </div>
      } @else if (allRequests().length === 0) {
      <div
        class="table-status table-status--empty flex flex-col align-center justify-center text-center"
        aria-live="polite"
      >
        <h3>
          {{ hasActiveSearch() ? 'No search results' : hasActiveFilters() ? 'No matching requests' :
          'No unlinked accounts' }}
        </h3>
        <p>
          {{ hasActiveSearch() ? 'Your search did not return any requests. Try a different name,
          DOB, or identifier.' : hasActiveFilters() ? 'There are no requests within the selected
          filters. Try adjusting or clearing the filters.' : 'There are no unlinked portal accounts
          within the selected filters. Try broadening the date range.' }}
        </p>
      </div>
      } @else {
      <div class="queue-table" role="table" aria-label="New linking requests">
        <div class="queue-table__header queue-table__grid gap-12 fw-600 uppercase" role="row">
          <div role="columnheader">Submitted</div>
          <div role="columnheader">Patient Name</div>
          <div role="columnheader">DOB</div>
          <div role="columnheader">Identifier</div>
          <div role="columnheader">Assigned To</div>
          <div role="columnheader">Status</div>
        </div>

        <div class="queue-table__body" role="rowgroup">
          @for (request of allRequests(); track request.id) {
          <div
            class="queue-table__row queue-table__grid align-center gap-12 cursor"
            role="row"
            tabindex="0"
            [class.queue-table__row--selected]="isRowSelected(request.id)"
            [attr.aria-label]="request.ariaLabel"
            (click)="openRequest(request.id)"
            (keydown)="onRowKeydown($event, request.id)"
          >
            <div class="cell cell--secondary" role="cell">
              {{ getRelativeSubmittedAt(request) }}
            </div>
            <div class="cell cell--primary fw-600" role="cell">{{ request.patientName }}</div>
            <div class="cell cell--primary" role="cell">{{ request.dob }}</div>
            <div
              class="cell cell--primary"
              role="cell"
              [attr.aria-label]="request.identifierAriaLabel"
            >
              {{ request.identifier }}
            </div>
            <div class="cell" role="cell">
              <span class="assign-tag" [class.assign-tag--assigned]="request.assignedToDisplayName">
                <app-icon
                  name="account_circle"
                  size="xs"
                  color="custom"
                  customColor="currentColor"
                  role="presentation"
                />
                {{ request.assignedToDisplayName ?? 'Unassigned' }}
              </span>
            </div>
            <div class="cell" role="cell">
              <span class="badge" [class]="'badge--' + request.statusKind"
                >{{ request.statusLabel }}</span
              >
            </div>
          </div>
          }
        </div>
      </div>

      <footer class="pagination flex align-center justify-between gap-12" aria-label="Pagination">
        <p class="pagination__summary m-0 fs-12">{{ showingSummary() }}</p>

        <nav
          class="pagination__controls flex flex-wrap justify-end gap-4"
          aria-label="Page controls"
        >
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(1)"
            [disabled]="currentPage() === 1"
            aria-label="First page"
          >
            <app-icon
              name="first_page"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(currentPage() - 1)"
            [disabled]="currentPage() === 1"
            aria-label="Previous page"
          >
            <app-icon
              name="chevron_left"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>

          @for (pageItem of pageItems(); track $index) { @if (pageItem === 'ellipsis') {
          <span class="pagination__ellipsis fs-12" aria-hidden="true">…</span>
          } @else {
          <button
            type="button"
            class="page-button fs-12 fw-600 cursor"
            [class.page-button--active]="pageItem === currentPage()"
            [attr.aria-current]="pageItem === currentPage() ? 'page' : null"
            (click)="goToPage(pageItem)"
          >
            {{ pageItem }}
          </button>
          } }

          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(currentPage() + 1)"
            [disabled]="currentPage() === totalPages()"
            aria-label="Next page"
          >
            <app-icon
              name="chevron_right"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(totalPages())"
            [disabled]="currentPage() === totalPages()"
            aria-label="Last page"
          >
            <app-icon
              name="last_page"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
        </nav>
      </footer>
      }
    </div>
  </section>
</section>
